/*
 * F4M tracking: interaction events for the custom pixel.
 *
 * Publishes to Shopify's customer events bus only (Shopify.analytics.publish).
 * Nothing leaves the browser from here: the custom pixel "f4m-gtm" subscribes
 * to these events and forwards them to GTM, and Shopify creates that pixel only
 * after consent. Do not add network calls to third parties in this file, a
 * theme script runs before consent (docs/12 §12.3.11a).
 *
 * Event names carry the prefix "f4m_"; the pixel strips it.
 * Spec: tracking/README.md
 */
(() => {
  const PREFIX = 'f4m_';
  const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100];
  const TEXT_LIMIT = 100;

  const emit = (name, data) => {
    const analytics = window.Shopify && window.Shopify.analytics;
    if (analytics && typeof analytics.publish === 'function') {
      analytics.publish(PREFIX + name, data || {});
    }
  };

  const clean = (text) => (text || '').replace(/\s+/g, ' ').trim().slice(0, TEXT_LIMIT);

  const slug = (text) =>
    clean(text)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40);

  const labelOf = (element) => {
    const image = element.querySelector('img[alt]');
    return (
      clean(element.innerText) ||
      clean(element.getAttribute('aria-label')) ||
      clean(element.getAttribute('title')) ||
      clean(element.value) ||
      (image ? clean(image.getAttribute('alt')) : '')
    );
  };

  // An explicit data-mq-id wins. Otherwise derive a stable id from the section
  // (template ids stripped) and the element label, so equal texts in different
  // sections stay distinguishable.
  const mqIdOf = (element) => {
    const tagged = element.closest('[data-mq-id]');
    if (tagged) return tagged.getAttribute('data-mq-id');
    const section = element.closest('[id^="shopify-section-"]');
    const sectionId = section
      ? section.id.replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, '')
      : 'layout';
    return `${sectionId}:${slug(labelOf(element)) || element.tagName.toLowerCase()}`;
  };

  const classOf = (element) =>
    clean(typeof element.className === 'string' ? element.className : '');

  // Buttons have no href, tel: and mailto: links no hostname; both yield ''.
  const domainOf = (element) => {
    if (typeof element.href !== 'string' || !element.href) return '';
    try {
      return new URL(element.href, window.location.href).hostname;
    } catch (error) {
      return '';
    }
  };

  const isInternal = (link) => domainOf(link) === window.location.hostname;

  const NAVIGATION = '.header__inline-menu, .menu-drawer, header-drawer, .mega-menu';
  const FOOTER = 'footer, .footer, [id$="__footer"]';
  const ACCOUNT = 'shopify-account, .header__account, .header__icon--account';
  const BUTTON = 'button, [role="button"], input[type="submit"], .button, .btn';

  const clickEventOf = (element) => {
    const href = element.getAttribute('href') || '';
    if (href.startsWith('tel:')) return 'phone';
    if (href.startsWith('mailto:')) return 'mail';
    if (element.closest(ACCOUNT)) return 'account';
    if (element.tagName === 'A' && element.closest(NAVIGATION)) return 'navigation';
    if (element.tagName === 'A' && element.closest(FOOTER)) return 'footer_link';
    if (element.matches(BUTTON)) return 'button';
    if (element.tagName === 'A') return isInternal(element) ? 'internal_link' : 'outbound_link';
    return null;
  };

  document.addEventListener(
    'click',
    (event) => {
      const element = event.target.closest(`a[href], button, [role="button"], input[type="submit"], ${ACCOUNT}`);
      if (!element) return;
      const name = clickEventOf(element);
      if (!name) return;
      const domain = domainOf(element);
      emit(name, {
        event_action: 'click',
        link_text: labelOf(element),
        link_url: element.href || '',
        link_classes: classOf(element),
        link_id: element.id || '',
        link_domain: domain,
        outbound: domain !== '' && domain !== window.location.hostname,
        data_mq_id: mqIdOf(element),
      });
    },
    true
  );

  // Flyouts: desktop menus open on hover, the mobile drawer uses <details>.
  const flyoutSeen = new WeakMap();
  const emitFlyout = (item, level) => {
    const now = Date.now();
    if (now - (flyoutSeen.get(item) || 0) < 1000) return;
    flyoutSeen.set(item, now);
    const trigger = item.querySelector('a, summary') || item;
    emit('navigation', {
      event_action: 'flyout',
      level,
      link_text: labelOf(trigger),
      data_mq_id: mqIdOf(trigger),
    });
  };

  document.querySelectorAll('.list-menu--megamenu, .list-menu--submenu').forEach((item) => {
    item.addEventListener('mouseenter', () => emitFlyout(item, 1));
  });

  // A <details> rendered open fires a toggle on page load. Accordions count only
  // after a click on their summary, which keyboard activation triggers as well.
  const toggledByVisitor = new WeakSet();
  document.addEventListener(
    'click',
    (event) => {
      const summary = event.target.closest && event.target.closest('summary');
      if (summary && summary.parentElement instanceof HTMLDetailsElement) toggledByVisitor.add(summary.parentElement);
    },
    true
  );

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      const summary = details.querySelector('summary');
      if (details.closest(NAVIGATION)) {
        if (details.open) emitFlyout(details, details.parentElement.closest('details') ? 2 : 1);
        return;
      }
      if (details.closest('header, .header, cart-drawer, .facets, localization-form')) return;
      if (!toggledByVisitor.has(details)) return;
      toggledByVisitor.delete(details);
      emit('accordion', {
        event_action: details.open ? 'open' : 'close',
        link_text: summary ? labelOf(summary) : '',
        data_mq_id: mqIdOf(summary || details),
      });
    },
    true
  );

  // Scroll depth of the page, each threshold once.
  const reached = new Set();
  let scrollPending = false;
  const measureScroll = () => {
    scrollPending = false;
    const root = document.scrollingElement || document.documentElement;
    const scrollable = root.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const percent = ((window.scrollY + window.innerHeight) / root.scrollHeight) * 100;
    SCROLL_THRESHOLDS.forEach((threshold) => {
      if (percent >= threshold - 0.5 && !reached.has(threshold)) {
        reached.add(threshold);
        emit('scroll', { percent_scrolled: threshold });
      }
    });
  };
  window.addEventListener(
    'scroll',
    () => {
      if (scrollPending) return;
      scrollPending = true;
      window.requestAnimationFrame(measureScroll);
    },
    { passive: true }
  );

  // Forms. Cart, product and search forms are commerce events, not form events.
  const isTrackedForm = (form) => {
    const action = form.getAttribute('action') || '';
    return !/\/cart|\/search/.test(action) && form.getAttribute('role') !== 'search';
  };
  const formData = (form) => ({
    form_id: form.id || '',
    form_name: form.getAttribute('name') || '',
    form_destination: form.action || '',
  });

  const startedForms = new WeakSet();
  document.addEventListener(
    'focusin',
    (event) => {
      const form = event.target.form || event.target.closest('form');
      if (!form || startedForms.has(form) || !isTrackedForm(form)) return;
      startedForms.add(form);
      emit('form_start', formData(form));
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !isTrackedForm(form)) return;
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      emit('form_submit', { ...formData(form), form_submit_text: submitter ? labelOf(submitter) : '' });
    },
    true
  );

  // Successful submits: Shopify redirects back with a marker in the query string.
  const query = new URLSearchParams(window.location.search);
  if (query.get('contact_posted') === 'true') emit('contact_submit', { form_id: window.location.hash.slice(1) });
  if (query.get('customer_posted') === 'true') emit('newsletter_signup', { form_id: window.location.hash.slice(1) });

  // Filters and sorting on collection and search pages.
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input.closest || !input.closest('form[id^="FacetFiltersForm"], facet-filters-form')) return;
      if (input.name === 'sort_by') {
        emit('sort', { sort_value: input.value });
      } else if (input.name && input.name.startsWith('filter.')) {
        emit('filter', {
          filter_name: input.name,
          filter_value: input.value,
          event_action: input.type === 'checkbox' && !input.checked ? 'remove' : 'apply',
        });
      }
    },
    true
  );

  // The cart drawer: Shopify's cart_viewed only covers the /cart page.
  const toItem = (line) => ({
    item_id: line.sku || String(line.variant_id),
    item_name: line.product_title,
    item_variant: line.variant_title || undefined,
    item_brand: line.vendor,
    item_category: line.product_type || undefined,
    price: line.final_price / 100,
    quantity: line.quantity,
    product_id: String(line.product_id),
    variant_id: String(line.variant_id),
  });

  const emitDrawerView = () =>
    fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) =>
        emit('view_cart', {
          ecommerce: {
            currency: cart.currency,
            value: cart.total_price / 100,
            items: cart.items.map(toItem),
          },
          cart_type: 'drawer',
        })
      )
      .catch((error) => console.warn('f4m-tracking: cart drawer view not published', error));

  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    let open = drawer.classList.contains('active');
    new MutationObserver(() => {
      const nowOpen = drawer.classList.contains('active');
      if (nowOpen && !open) emitDrawerView();
      open = nowOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }
})();
